/**
 * The archived PDF of an ISSUED invoice — Phase 22, Task 22.5 (PRD 11.7).
 *
 * ═══ WHY THIS IS NOT AN HTML RENDERING ═══
 *
 * KD-081d: once an invoice leaves `draft` the screen shows the REAL archived
 * PDF, never a replica. The legal document is produced by
 * `services/invoice_pdf.py` and frozen to disk at issue; an HTML re-rendering
 * of it would be a SECOND RENDERER, and any drift between the two — one
 * field, one rounding rule — would mean the admin reads one document on
 * screen and their customer receives a different one. Showing the archived
 * bytes has zero divergence risk, because the bytes ARE the document.
 *
 * ═══ HOW THE BYTES GET HERE ═══
 *
 * Through `downloadInvoicePdf`, which is an authenticated, tenant-isolated
 * request. A direct `<object data="/api/invoices/.../pdf">` is deliberately
 * NOT constructed: the browser would issue that request with no Authorization
 * header and the frame would simply be empty.
 *
 * Every object URL created here is revoked — on unmount, on an id change and
 * on a retry — so navigating in and out of this page repeatedly cannot leak
 * blobs.
 *
 * ═══ FAILURE IS NEVER SILENT ═══
 *
 * A failed fetch lands on a stated error with a retry. It must never be a
 * blank frame and never an endless spinner: this is a legal document, and
 * "nothing rendered" must not be mistakable for "there is no document".
 *
 * There is NO editing affordance anywhere in this component, by construction —
 * an issued invoice is immutable (PRD 10.8) and the backend refuses every
 * change to it, so the UI does not present one.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileWarning, Loader2, RotateCw } from 'lucide-react';
import { downloadInvoicePdf } from '../lib/api';
import { getApiErrorMessage } from '../lib/errors';
import { Button } from './ui/button';
import './invoice-a4.css';

export default function InvoicePdfFrame({ invoiceId, onDownload, downloading = false }) {
    const { t } = useTranslation();
    // status: 'loading' | 'ready' | 'error'
    // The FAILURE ITSELF is kept, not a translated message: resolving it here
    // would put `t` in the effect's dependencies, and a language switch would
    // then throw away a loaded document and refetch it. The wording is produced
    // at render instead, where it also follows a language switch.
    const [state, setState] = useState({ status: 'loading', url: null, error: null });
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = null;

        setState({ status: 'loading', url: null, error: null });

        if (!invoiceId) {
            setState({ status: 'error', url: null, error: null });
            return undefined;
        }

        downloadInvoicePdf(invoiceId)
            .then((response) => {
                if (cancelled) return;
                // An explicit type is set on the blob: `<object>` dispatches on
                // it, and an octet-stream would be offered as a download rather
                // than rendered in place.
                objectUrl = URL.createObjectURL(
                    new Blob([response.data], { type: 'application/pdf' })
                );
                setState({ status: 'ready', url: objectUrl, error: null });
            })
            .catch((error) => {
                if (cancelled) return;
                setState({ status: 'error', url: null, error });
            });

        return () => {
            cancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
                objectUrl = null;
            }
        };
    }, [invoiceId, reloadKey]);

    const retry = useCallback(() => setReloadKey((previous) => previous + 1), []);

    // `getApiErrorMessage` resolves `error.*` keys itself — its result is never
    // put through `t` a second time.
    const errorMessage = state.error
        ? getApiErrorMessage(state.error, t('invoices.pdf_error'))
        : t('invoices.pdf_error');

    return (
        <div className="border border-border" data-testid="invoice-pdf-frame">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b border-border">
                <div>
                    <p className="label-uppercase text-muted-foreground">
                        {t('invoices.pdf_title')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                        {t('invoices.pdf_archived_notice')}
                    </p>
                </div>
                {onDownload && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onDownload}
                        disabled={downloading}
                        data-testid="invoice-pdf-frame-download"
                    >
                        {downloading
                            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            : <Download className="w-4 h-4 mr-2" />}
                        {t('invoices.download_pdf')}
                    </Button>
                )}
            </div>

            {state.status === 'loading' && (
                <div
                    className="flex flex-col items-center justify-center gap-2 p-12 text-muted-foreground"
                    data-testid="invoice-pdf-loading"
                >
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <p className="text-sm">{t('invoices.pdf_loading')}</p>
                </div>
            )}

            {state.status === 'error' && (
                <div
                    className="flex flex-col items-center justify-center gap-3 p-12 text-center"
                    data-testid="invoice-pdf-error"
                >
                    <FileWarning className="w-8 h-8 text-destructive" />
                    <p className="text-sm">{errorMessage}</p>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={retry}
                        data-testid="invoice-pdf-retry"
                    >
                        <RotateCw className="w-4 h-4 mr-2" />
                        {t('invoices.pdf_retry')}
                    </Button>
                </div>
            )}

            {state.status === 'ready' && state.url && (
                <object
                    data={state.url}
                    type="application/pdf"
                    className="inv-pdf-frame"
                    aria-label={t('invoices.pdf_alt')}
                >
                    {/* Shown by the browser only when it cannot display a PDF
                        inline — a real fallback with a real way out, never a
                        blank rectangle. */}
                    <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
                        <p className="text-sm text-muted-foreground">
                            {t('invoices.pdf_fallback')}
                        </p>
                        {onDownload && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={onDownload}
                                disabled={downloading}
                            >
                                <Download className="w-4 h-4 mr-2" />
                                {t('invoices.download_pdf')}
                            </Button>
                        )}
                    </div>
                </object>
            )}
        </div>
    );
}
